import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFirestore } from 'firebase/firestore';
import { collection, doc, getDoc, query, onSnapshot } from 'firebase/firestore';
import './TreePage.css';
import MemoryCarousel from './MemoryCarousel';

const db = getFirestore();

const getRandomColor = () => {
  const colors = [
    '#124611', // Vibrant red
    '#6A0572', // Festive purple
    '#8B0000', // Christmas green
    '#DAA520', // Golden yellow
    '#E9C46A', // Soft gold
    '#D7263D', // Bold red
  ];
  return colors[Math.floor(Math.random() * colors.length)];
};


const getRandomPointInTriangle = (A, B, C) => {
  let r1 = Math.random();
  let r2 = Math.random();

  if (r1 > r2) {
    [r1, r2] = [r2, r1];
  }

  let a = r1;
  let b = r2 - r1;
  let c = 1 - r2;

  const x = Math.round(a * A[0] + b * B[0] + c * C[0]);
  const y = Math.round(a * A[1] + b * B[1] + c * C[1]);

  return [x, y];
};

const TreePage = () => {
  const { treeId } = useParams();
  const navigate = useNavigate();
  const [tree, setTree] = useState(null);
  const [notes, setNotes] = useState([]);
  const [error, setError] = useState('');
  const [showCarousel, setShowCarousel] = useState(false);
  const [currentNoteIndex, setCurrentNoteIndex] = useState(0);
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [showCopyNotification, setShowCopyNotification] = useState(false);

  const calculateScaledPosition = (x, y) => {
    const scale = 650 / 800;
    const scaledX = x * scale;
    const scaledY = y * scale;

    return {
      left: `${(scaledX / 650) * 100}%`,
      top: `${(scaledY / 650) * 100}%`,
    };
  };

  const positions = useMemo(() => {
    const topPoint = [407, 161];
    const bottomLeft = [181, 667];
    const bottomRight = [630, 666];

    return Array(20)
      .fill(null)
      .map(() => {
        const [x, y] = getRandomPointInTriangle(topPoint, bottomLeft, bottomRight);
        return {
          ...calculateScaledPosition(x, y),
          color: getRandomColor(),
        };
      });
  }, []);

  useEffect(() => {
    const fetchTree = async () => {
      try {
        const treeDoc = await getDoc(doc(db, 'trees', treeId));
        if (treeDoc.exists()) {
          setTree(treeDoc.data());
        } else {
          setError('Tree not found');
        }
      } catch (err) {
        console.error(err);
        setError('Failed to fetch tree data');
      }
    };
    fetchTree();
  }, [treeId]);

  useEffect(() => {
    if (!treeId) return;
    const notesQuery = query(collection(db, 'trees', treeId, 'notes'));
    const unsubscribe = onSnapshot(notesQuery, (snapshot) => {
      const fetchedNotes = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setNotes(fetchedNotes);
    });
    return () => unsubscribe();
  }, [treeId]);

  const handleCopyLink = () => {
    const link = `${window.location.origin}/shared/${treeId}`;
    navigator.clipboard.writeText(link);
    setShowCopyNotification(true);
    setTimeout(() => setShowCopyNotification(false), 2000);
  };

  const redirectToNotesPage = () => {
    navigate(`/tree/${treeId}/notes`, {
      state: { notes, treeName: tree?.treeName },
    });
  };

  const openCarousel = (noteId) => {
      if (noteId) {
          const index = notes.findIndex((note) => note.id === noteId);
          setCurrentNoteIndex(index);
      } else {
          setCurrentNoteIndex(0)
      }
      setShowCarousel(true);
  };

  const closeCarousel = () => {
    setShowCarousel(false);
    setSelectedNoteId(null);
  };

    const handleDecorationClick = (noteId, e) => {
        e.stopPropagation();
        setSelectedNoteId(noteId);
        openCarousel(noteId);
    };

    const handleTreeClick = () => {
        openCarousel(null)
    };

  if (error) {
    return (
      <div className="christmas-tree-page">
        <p className="error-message">
          Oh we are so sorry, but this tree is not available.
        </p>
      </div>
    );
  }

  if (!tree) {
    return (
      <div className="loading-container">
        <img
          src={`${process.env.PUBLIC_URL}/loading.gif`}
          alt="Christmas Tree"
          className="loading-tree"
        />
      </div>
    );
  }

  return (
    <div className="christmas-tree-page">
      <div className="lights-container">
        <video
          className="christmas-lights-video"
          autoPlay
          loop
          muted
          playsInline
          src={`${process.env.PUBLIC_URL}/christmas_lights.mp4`}
        >
          <source src={`${process.env.PUBLIC_URL}/christmas_lights.mp4`} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>

      <header className="christmas-header">
        <h1 className="tree-title">{tree.treeName}</h1>
        <div className="tree-actions">
          <button className="christmas-button" onClick={redirectToNotesPage}>
            🎄 View Memories
          </button>
          <button className="christmas-button" onClick={handleCopyLink}>
            🔗 Share Tree
          </button>
        </div>
      </header>

      <div className="tree-container" onClick={handleTreeClick}>
        <img
          src={`${process.env.PUBLIC_URL}/tree-empty.png`}
          alt="Christmas Tree"
        />
        {notes.map((note, index) => (
        <div
          key={note.id}
          className="decoration-ball"
          style={{
            left: positions[index]?.left,
            top: positions[index]?.top,
            backgroundImage: note.photoBase64
              ? `url(data:image/jpeg;base64,${note.photoBase64})`
              : 'none',
            backgroundColor: note.photoBase64 ? 'none' : getRandomColor(),
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            borderColor: 'var(--gold-accent)',
          }}
          onClick={(e) => handleDecorationClick(note.id, e)}
        >
          <div className="decoration-string"></div>
          {!note.photoBase64 && <div className="decoration-shine"></div>}
        </div>
        ))}


      </div>

      {showCarousel && (
          <MemoryCarousel
              notes={notes}
              onClose={closeCarousel}
              initialNoteIndex={currentNoteIndex}
          />
      )}

      {showCopyNotification && (
        <div className="copy-notification">
          🎉 Link copied to clipboard!
        </div>
      )}
    </div>
  );
};

export default TreePage;